import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Settings, Eye, MoreHorizontal } from "lucide-react";
import { H, MANROPE, FOCUS, BandChip, FLAG_LABELS, ddmon } from "../../healthcheck/hcTokens";
import { RichTextEditor } from "../../RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/health-check`;
const CS = `${process.env.REACT_APP_BACKEND_URL}/api/customer-success`;

const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: H.ink3 };
const INPUT = "w-full h-9 rounded-[7px] px-2.5 text-[13px] bg-white";
const EDGE = { border: `1px solid ${H.control}`, color: H.ink };
const GRID = "minmax(200px,1.5fr) 92px 62px 104px 70px 108px minmax(160px,1fr) 104px";

const RowMenu = ({ r, at, onPick }) => {
  const item = (key, label, hint, tone) => (
    <button key={key} type="button" data-testid={`hc-menu-${key}`}
      onClick={(e) => { e.stopPropagation(); onPick(key, r); }}
      className="w-full text-left px-3 py-2 rounded-[7px] hover:bg-[#F6F4EF]">
      <span className="block text-[13px] font-semibold" style={{ color: tone || H.ink }}>{label}</span>
      {hint && <span className="block text-[11.5px]" style={{ color: H.ink2 }}>{hint}</span>}
    </button>
  );
  const h = r.submission_id ? 248 : 206;
  const flip = at.bottom + h > window.innerHeight - 12;
  return (
    <div className="fixed p-1.5" data-testid={`hc-menu-${r.client_id}`}
      onClick={(e) => e.stopPropagation()}
      style={{ width: 268, zIndex: 80, background: H.card, border: `1px solid ${H.border}`,
        borderRadius: 11, boxShadow: "0 12px 32px rgba(26,26,26,0.16)",
        right: Math.max(12, window.innerWidth - at.right),
        top: flip ? undefined : at.bottom + 6,
        bottom: flip ? window.innerHeight - at.top + 6 : undefined }}>
      {r.submission_id && item("results", "View the results", "Score, answers and what it created")}
      {item("call", "Fill it in on a call", "Opens the staff form for this client")}
      {item("client", "Open the client", "Their record and timeline")}
      {item("invite", "Invite them again", "Review the email before it goes")}
      <div className="my-1 h-px" style={{ background: H.divider }} />
      {r.submission_id
        ? item("delete", "Delete this Health Check", "Removes the score, follow-ups and backlog items", "#8E1212")
        : item("cancel", "Cancel the invitation", "The link stops working and the chase closes", "#8E1212")}
    </div>
  );
};

const ConfirmDelete = ({ row, busy, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" data-testid="hc-delete-confirm">
    <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.32)" }} onClick={onCancel} />
    <div className="relative w-full max-w-[460px] rounded-[12px] p-5"
      style={{ background: H.card, border: `1px solid ${H.border}` }}>
      <div style={{ ...MANROPE, fontSize: 17, fontWeight: 800, color: H.ink }}>
        {row.submission_id ? `Delete the Health Check for ${row.company_name}?` : `Cancel the invitation to ${row.company_name}?`}
      </div>
      <div className="text-[13px] mt-2" style={{ color: H.ink2 }}>
        {row.submission_id ? (
          <>This removes the submission, its timeline entry, the pain points and feature asks it
            raised and the follow-up to-dos it created. {row.company_name}'s score, band and next
            check date go back to their previous check, or clear if this was their first. It can't
            be undone — it's for a test or a mistake, not a real conversation.</>
        ) : (
          <>Their personal link stops working and the chase to-do closes. You can invite them
            again at any time.</>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 mt-5">
        <button onClick={onCancel} className="h-9 px-3 text-[13px]" style={{ color: H.ink2 }}
          data-testid="hc-delete-cancel">Keep it</button>
        <button onClick={onConfirm} disabled={busy} data-testid="hc-delete-go"
          className={`h-9 px-4 rounded-[9px] text-[13px] font-bold ${FOCUS}`}
          style={{ background: "#8E1212", color: "#fff", opacity: busy ? 0.6 : 1 }}>
          {row.submission_id ? "Delete it" : "Cancel the invitation"}
        </button>
      </div>
    </div>
  </div>
);

const Num = ({ label, value, tone, testid }) => (
  <div className="px-4 py-3" style={{ borderRight: `1px solid ${H.divider}` }}>
    <div style={LABEL}>{label}</div>
    <div className="mono" data-testid={testid}
      style={{ fontSize: 22, fontWeight: 800, color: tone || H.ink, lineHeight: 1.2 }}>{value}</div>
  </div>
);

const RecipientPicker = ({ row, selected, onToggle, onAdd }) => {
  const [extra, setExtra] = useState("");
  const has = (email) => selected.some((s) => s.email.toLowerCase() === email.toLowerCase());
  return (
    <div className="px-3 py-2.5" style={{ borderTop: `1px solid ${H.divider}` }}
      data-testid={`hc-invite-recipient-${row.client_id}`}>
      <div className="text-[12.5px] font-semibold" style={{ color: H.ink }}>
        {row.company_name || row.client_id}
      </div>
      {row.problem && !row.contacts?.length ? (
        <div className="text-[11.5px] mt-0.5" style={{ color: "#8E1212" }}>{row.problem}</div>
      ) : (
        <>
          {(row.contacts || []).map((c) => (
            <label key={c.email} className="flex items-start gap-2 mt-1.5 cursor-pointer"
              data-testid={`hc-invite-contact-${row.client_id}-${c.email}`}>
              <input type="checkbox" checked={has(c.email)} onChange={() => onToggle(c)}
                className="w-4 h-4 mt-0.5" style={{ accentColor: H.accentEdge }} />
              <span className="text-[11.5px]" style={{ color: H.ink2 }}>
                <span style={{ color: H.ink, fontWeight: 600 }}>{c.name || c.email}</span>
                {c.role ? ` · ${c.role}` : ""}{c.is_primary ? " · primary" : ""}
                <br />{c.email}
              </span>
            </label>
          ))}
          {selected.filter((s) => !(row.contacts || []).some(
            (c) => c.email.toLowerCase() === s.email.toLowerCase())).map((s) => (
            <label key={s.email} className="flex items-start gap-2 mt-1.5 cursor-pointer">
              <input type="checkbox" checked onChange={() => onToggle(s)}
                className="w-4 h-4 mt-0.5" style={{ accentColor: H.accentEdge }} />
              <span className="text-[11.5px]" style={{ color: H.ink2 }}>{s.email} · added by hand</span>
            </label>
          ))}
          <div className="flex items-center gap-1.5 mt-2">
            <input value={extra} onChange={(e) => setExtra(e.target.value)}
              data-testid={`hc-invite-add-${row.client_id}`}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); if (onAdd(extra)) setExtra(""); }
              }}
              placeholder="Another address…"
              className={`h-7 flex-1 rounded-[6px] px-2 text-[11.5px] bg-white ${FOCUS}`} style={EDGE} />
            <button type="button" onClick={() => { if (onAdd(extra)) setExtra(""); }}
              className="h-7 px-2 rounded-[6px] text-[11px] font-bold uppercase"
              style={{ background: "#fff", border: `1px solid ${H.control}`, color: H.ink2 }}>Add</button>
          </div>
        </>
      )}
    </div>
  );
};

const renderTokens = (html, ctx) => {
  let out = html || "";
  Object.entries(ctx).forEach(([k, v]) => {
    out = out.split(`{{${k}}}`).join(v || "");
  });
  return out;
};

const InviteDialog = ({ onClose, onSent, preselect }) => {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(
    Array.isArray(preselect) ? preselect : preselect ? [preselect] : []);
  const [teamMember, setTeamMember] = useState("");
  const [step, setStep] = useState("pick");
  const [draft, setDraft] = useState(null);     // { subject, body_html, recipients, ... }
  const [resetKey, setResetKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    axios.get(`${CS}/clients/register`).then((r) => setClients(r.data.clients || [])).catch(() => {});
  }, []);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return clients.filter((c) => (c.company_name || "").toLowerCase().includes(t)
      || (c.client_id || "").toLowerCase().includes(t)).slice(0, 8);
  }, [q, clients]);

  const loadPreview = async (bodyOverride) => {
    if (!picked.length) { toast.error("Pick at least one client"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/invite/preview`, {
        client_ids: picked.map((p) => p.client_id),
        team_member: teamMember || null,
        subject: draft?.subject || null,
        body_html: bodyOverride === null ? null : (bodyOverride ?? draft?.body_html ?? null),
      });
      setDraft(r.data);
      setResetKey((k) => k + 1);
      setStep("review");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the preview"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/invite`, {
        client_ids: (draft.recipients || []).filter((x) => (x.to || []).length).map((x) => x.client_id),
        team_member: draft.team_member || null,
        recipients: Object.fromEntries((draft.recipients || [])
          .filter((x) => (x.to || []).length).map((x) => [x.client_id, x.to])),
        subject: draft.subject, body_html: draft.body_html,
      });
      const sent = r.data.sent?.length || 0;
      if (sent) toast.success(`Invitation sent to ${sent} contact${sent > 1 ? "s" : ""}`);
      (r.data.skipped || []).forEach((s) =>
        toast.error(`${s.company_name || s.client_id}: ${s.reason}`));
      onSent(); if (sent) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the invitation"); }
    finally { setBusy(false); }
  };

  const setTo = (cid, to) => setDraft((d) => ({
    ...d,
    recipients: d.recipients.map((r) => (r.client_id === cid ? { ...r, to } : r)),
    sendable: d.recipients.reduce(
      (n, r) => n + (r.client_id === cid ? to.length : (r.to || []).length), 0),
  }));

  const toggleContact = (row, c) => {
    const on = (row.to || []).some((s) => s.email.toLowerCase() === c.email.toLowerCase());
    setTo(row.client_id, on
      ? row.to.filter((s) => s.email.toLowerCase() !== c.email.toLowerCase())
      : [...(row.to || []), { name: c.name || null, email: c.email }]);
  };

  const addContact = (row, raw) => {
    const email = (raw || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)) {
      toast.error("That doesn't look like an email address");
      return false;
    }
    if ((row.to || []).some((s) => s.email.toLowerCase() === email.toLowerCase())) return true;
    setTo(row.client_id, [...(row.to || []), { name: null, email }]);
    return true;
  };

  const testLink = async (emailIt) => {
    const row = (draft.recipients || []).find((r) => r.company_name) || {};
    if (!row.client_id) { toast.error("Pick a client first"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/invite/test`, {
        client_id: row.client_id, subject: draft.subject, body_html: draft.body_html,
        email: emailIt || null,
      });
      if (emailIt) toast.success(`Test invitation emailed to ${emailIt}`);
      else {
        // Open it on whatever host we're on, so a test from the preview opens
        // the preview form rather than the live one.
        const url = new URL(r.data.link);
        window.open(`${window.location.origin}${url.pathname}${url.search}`, "_blank", "noopener");
        toast.success("Test link opened — nothing you submit there is recorded");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create a test link"); }
    finally { setBusy(false); }
  };

  const ctx = {
    first_name: ((draft?.recipients?.find((r) => (r.to || []).length)?.to?.[0]?.name)
      || "there").split(" ")[0],
    company_name: draft?.preview_for || "",
    link: `${window.location.origin}/health-check/EAG123?t=example-link`,
    team_member: draft?.team_member || "", call_from: draft?.call_from || "",
    md_name: draft?.md_name || "",
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center px-4 py-10 overflow-y-auto"
      data-testid="hc-invite-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.3)" }} onClick={onClose} />
      <div className={`relative w-full ${step === "review" ? "max-w-[860px]" : "max-w-[520px]"} rounded-[12px] overflow-hidden`}
        style={{ background: H.card, border: `1px solid ${H.border}` }}>
        <div className="px-5 py-4 flex items-start justify-between" style={{ borderBottom: `1px solid ${H.divider}` }}>
          <div>
            <div style={{ ...MANROPE, fontSize: 17, fontWeight: 800, color: H.ink }}>
              {step === "pick" ? "Invite clients to a Health Check" : "Review before it goes out"}
            </div>
            <div className="text-[12.5px] mt-0.5" style={{ color: H.ink2 }}>
              {step === "pick"
                ? "Pick who it goes to, then you'll see exactly what they'll receive before anything is sent."
                : "Edit the wording as much as you like. Each client gets their own personal link, and nothing is sent until you approve it here."}
            </div>
          </div>
          <button onClick={onClose} data-testid="hc-invite-close" className="w-8 h-8 flex items-center justify-center"
            style={{ color: H.ink2 }}><X className="w-4 h-4" /></button>
        </div>

        {step === "pick" ? (
          <div className="px-5 py-4 space-y-4">
            <div>
              <div style={LABEL}>Find a client</div>
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hc-invite-search"
                placeholder="Company name or client ID" className={`${INPUT} ${FOCUS} mt-1`} style={EDGE} />
              {shown.length > 0 && (
                <div className="mt-1.5 rounded-[7px] overflow-hidden" style={{ border: `1px solid ${H.divider}` }}>
                  {shown.map((c) => (
                    <button key={c.client_id} data-testid={`hc-invite-pick-${c.client_id}`}
                      onClick={() => { setPicked((p) => p.some((x) => x.client_id === c.client_id) ? p : [...p, c]); setQ(""); }}
                      className="w-full text-left px-3 py-2 text-[13px] hover:bg-[#F6F4EF]" style={{ color: H.ink }}>
                      {c.company_name} <span className="mono text-[11.5px]" style={{ color: H.ink3 }}>{c.client_id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {picked.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="hc-invite-picked">
                {picked.map((p) => (
                  <span key={p.client_id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[12px]"
                    style={{ background: H.sunken, border: `1px solid ${H.divider}`, color: H.ink }}>
                    {p.company_name}
                    <button onClick={() => setPicked(picked.filter((x) => x.client_id !== p.client_id))}
                      style={{ color: H.ink3 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <div>
              <div style={LABEL}>Team member named in the email</div>
              <input value={teamMember} onChange={(e) => setTeamMember(e.target.value)}
                data-testid="hc-invite-team" placeholder="Who will call them" className={`${INPUT} ${FOCUS} mt-1`} style={EDGE} />
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
            <div className="min-w-0 space-y-3">
              <div>
                <div style={LABEL}>Subject</div>
                <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  data-testid="hc-invite-subject" className={`${INPUT} ${FOCUS} mt-1`} style={EDGE} />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <div style={{ ...LABEL, whiteSpace: "nowrap" }}>The email</div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(draft.tokens || []).map((t) => (
                      <button key={t} type="button" onClick={() => editorRef.current?.insertToken(t)}
                        data-testid={`hc-invite-token-${t.replace(/[{}]/g, "")}`}
                        className="text-[11px] px-1.5 py-0.5 rounded-full"
                        style={{ background: H.sunken, border: `1px solid ${H.divider}`, color: H.ink2 }}>
                        {t.replace(/[{}]/g, "")}
                      </button>
                    ))}
                  </div>
                </div>
                <RichTextEditor ref={editorRef} value={draft.body_html} resetKey={resetKey}
                  minHeight={300} onChange={(html) => setDraft((d) => ({ ...d, body_html: html }))} />
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-[11.5px]" style={{ color: H.ink3 }}>
                    Merge fields are filled in per client when it sends. Your own signature is
                    added underneath automatically.
                  </span>
                  <button type="button" onClick={() => loadPreview(null)} data-testid="hc-invite-reset"
                    className="text-[11.5px] font-semibold" style={{ color: H.accentInk }}>
                    Back to the standard wording
                  </button>
                </div>
              </div>
            </div>

            <div className="min-w-0 space-y-3">
              <div className="rounded-[9px] overflow-hidden" style={{ border: `1px solid ${H.divider}` }}>
                <div className="px-3 py-2" style={{ background: H.sunken }}>
                  <div style={LABEL}>How it will read</div>
                  <div className="text-[11.5px] mt-0.5" style={{ color: H.ink2 }}>
                    {draft.preview_for ? `Shown as ${draft.preview_for} will see it` : "Example"}
                    {draft.from_email ? ` · from ${draft.from_name || ""} <${draft.from_email}>` : ""}
                  </div>
                </div>
                <div className="px-3 py-3 max-h-[320px] overflow-y-auto bg-white" data-testid="hc-invite-preview"
                  dangerouslySetInnerHTML={{ __html: renderTokens(draft.body_html, ctx) + (draft.signature_html || "") }} />
              </div>
              <div className="rounded-[9px] overflow-hidden" style={{ border: `1px solid ${H.divider}` }}>
                <div className="px-3 py-2" style={{ background: H.sunken }}>
                  <div style={LABEL}>Going to {draft.sendable} contact{draft.sendable === 1 ? "" : "s"}</div>
                  <div className="text-[11.5px] mt-0.5" style={{ color: H.ink2 }}>
                    Tick everyone who should get it — each person gets their own personal link.
                  </div>
                </div>
                <div className="max-h-[260px] overflow-y-auto" data-testid="hc-invite-recipients">
                  {draft.recipients.map((r) => (
                    <RecipientPicker key={r.client_id} row={r} selected={r.to || []}
                      onToggle={(c) => toggleContact(r, c)}
                      onAdd={(raw) => addContact(r, raw)} />
                  ))}
                </div>
              </div>
              <div className="rounded-[9px] px-3 py-3" style={{ border: `1px solid ${H.divider}`, background: H.panel }}>
                <div style={LABEL}>Test it first</div>
                <div className="text-[11.5px] mt-0.5 mb-2" style={{ color: H.ink2 }}>
                  Opens a real, working link for {draft.preview_for || "this client"}. Anything
                  submitted through a test link is discarded.
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => testLink(null)} disabled={busy} data-testid="hc-invite-test-open"
                    className="h-8 px-3 rounded-[7px] text-[11.5px] font-bold uppercase disabled:opacity-60"
                    style={{ background: "#fff", border: `1px solid ${H.control}`, color: H.ink }}>
                    Open a test link
                  </button>
                  <button onClick={() => testLink(window.prompt("Email the test to which address?", ""))}
                    disabled={busy} data-testid="hc-invite-test-email"
                    className="text-[11.5px] font-semibold" style={{ color: H.accentInk }}>
                    Email a test
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: H.panel, borderTop: `1px solid ${H.divider}` }}>
          {step === "review" ? (
            <button onClick={() => setStep("pick")} className="h-9 px-3 text-[13px]" data-testid="hc-invite-back"
              style={{ color: H.ink2 }}>Back</button>
          ) : (
            <button onClick={onClose} className="h-9 px-3 text-[13px]" style={{ color: H.ink2 }}>Cancel</button>
          )}
          {step === "pick" ? (
            <button onClick={() => loadPreview()} disabled={busy || !picked.length} data-testid="hc-invite-preview-btn"
              className="ml-auto h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase inline-flex items-center gap-1.5 disabled:opacity-60"
              style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.05em" }}>
              <Eye className="w-3.5 h-3.5" /> {busy ? "Building…" : "Preview the email"}
            </button>
          ) : (
            <button onClick={send} disabled={busy || !draft.sendable} data-testid="hc-invite-send"
              className="ml-auto h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase inline-flex items-center gap-1.5 disabled:opacity-60"
              style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.05em" }}>
              <Send className="w-3.5 h-3.5" />
              {busy ? "Sending…" : `Approve and send${draft.sendable > 1 ? ` to ${draft.sendable}` : ""}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SettingsDrawer = ({ onClose }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/settings`).then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/settings`, s); toast.success("Saved"); onClose(); }
    catch { toast.error("Could not save those settings"); }
    finally { setBusy(false); }
  };
  const field = (k, label, hint) => (
    <div>
      <div style={LABEL}>{label}</div>
      <input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })}
        data-testid={`hc-setting-${k}`} className={`${INPUT} ${FOCUS} mt-1`} style={EDGE} />
      {hint && <div className="text-[11.5px] mt-1" style={{ color: H.ink2 }}>{hint}</div>}
    </div>
  );
  return (
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid="hc-settings-drawer">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.28)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[430px] flex flex-col"
        style={{ background: H.card, borderLeft: `1px solid ${H.border}` }}>
        <div className="px-5 py-4" style={{ borderBottom: `1px solid ${H.divider}` }}>
          <div style={{ ...MANROPE, fontSize: 17, fontWeight: 800, color: H.ink }}>Health Check notifications</div>
          <div className="text-[12.5px]" style={{ color: H.ink2 }}>Who hears about a Red band, a critical pain point and an Amber follow-up.</div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
          {!s ? <div className="text-[13px]" style={{ color: H.ink2 }}>Loading…</div> : (<>
            {field("md_name", "MD name", "Used in internal escalation emails. Invitations are now signed by whoever sends them.")}
            {field("md_email", "MD email", "Told the same day a client goes Red.")}
            {field("cs_lead_email", "Client Solutions lead email", "Notified on Amber.")}
            {field("completion_notify", "Told about every completed check",
              "Comma-separated. Everyone here gets the score, band, flags and follow-ups as soon as a check is completed.")}
            {field("product_lead_email", "Product lead email", "Told the same day about a critical pain point.")}
            {field("team_member", "Default team member", "Named in the invitation email.")}
            {field("privacy_policy_url", "Privacy policy link", "Shown beside the client's consent tick.")}
            {field("support_email", "Support email", "Shown on the 'we couldn't find your account' page.")}
          </>)}
        </div>
        <div className="px-5 py-3.5 flex gap-2" style={{ background: H.panel, borderTop: `1px solid ${H.divider}` }}>
          <button onClick={onClose} className="h-9 px-3 text-[13px]" style={{ color: H.ink2 }}>Cancel</button>
          <button onClick={save} disabled={busy || !s} data-testid="hc-settings-save"
            className="ml-auto h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase disabled:opacity-60"
            style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.05em" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const BacklogTable = ({ items }) => (
  <div className="rounded-[12px] overflow-hidden" style={{ background: H.card, border: `1px solid ${H.border}` }}
    data-testid="hc-backlog-table">
    <div className="px-4 py-2.5 grid gap-3 text-[10.5px] font-bold uppercase tracking-[0.1em]"
      style={{ gridTemplateColumns: "120px minmax(220px,1fr) 110px 96px 110px", background: H.sunken, color: H.ink3, borderBottom: `1px solid ${H.divider}` }}>
      <div>Kind</div><div>What they told us</div><div>Client</div><div>Severity</div><div>30-day clock</div>
    </div>
    {items.length === 0 ? (
      <div className="px-4 py-6 text-[13px] font-semibold" style={{ color: H.ink }}>
        Nothing waiting on an answer.
      </div>
    ) : items.map((b) => (
      <div key={b.id} className="px-4 py-3 grid gap-3 items-start" data-testid={`hc-backlog-${b.id}`}
        style={{ gridTemplateColumns: "120px minmax(220px,1fr) 110px 96px 110px", borderBottom: `1px solid ${H.divider}` }}>
        <div className="text-[12px] font-bold uppercase" style={{ color: H.ink2, letterSpacing: "0.04em" }}>
          {b.kind === "feature_request" ? "Feature ask" : "Pain point"}
        </div>
        <div className="text-[13px]" style={{ color: H.ink }}>
          {b.text}
          {b.workaround && <div className="text-[12px] mt-0.5" style={{ color: H.ink3 }}>Workaround: {b.workaround}</div>}
        </div>
        <a href={`/clients/${b.client_id}`} className="text-[12.5px]" style={{ color: H.accentInk }}>{b.company_name}</a>
        <div className="text-[12.5px]" style={{ color: H.ink2 }}>{b.severity || "—"}</div>
        <div className="text-[12.5px] font-semibold"
          style={{ color: b.overdue ? "#8E1212" : H.ink }}>
          {b.days_left === null ? "—" : b.overdue ? `${Math.abs(b.days_left)}d over` : `${b.days_left}d left`}
        </div>
      </div>
    ))}
  </div>
);

export default function HealthChecksPage() {
  const [data, setData] = useState(null);
  const [band, setBand] = useState(null);
  const [view, setView] = useState("checks");
  const [backlog, setBacklog] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [invite, setInvite] = useState(false);
  const [preselect, setPreselect] = useState(null);
  const [settings, setSettings] = useState(false);
  const [runQ, setRunQ] = useState("");
  const [clients, setClients] = useState([]);
  const [menu, setMenu] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openRow = (r) => {
    window.location.href = r.submission_id
      ? `/health-checks/${r.submission_id}` : `/health-check/${r.client_id}/staff`;
  };

  const removeRow = async (r) => {
    setDeleting(true);
    try {
      if (r.submission_id) {
        const res = await axios.delete(`${API}/submissions/${r.submission_id}`);
        const n = Object.values(res.data?.removed || {}).reduce((a, b) => a + b, 0);
        toast.success(`Health Check deleted — ${n} record${n === 1 ? "" : "s"} removed`);
      } else {
        await axios.delete(`${API}/invitation/${r.client_id}`);
        toast.success("Invitation cancelled");
      }
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete that");
    } finally { setDeleting(false); }
  };

  const rowAction = (key, r) => {
    setMenu(null);
    if (key === "results") window.location.href = `/health-checks/${r.submission_id}`;
    else if (key === "call") window.location.href = `/health-check/${r.client_id}/staff`;
    else if (key === "client") window.location.href = `/clients/${r.client_id}`;
    else if (key === "invite") {
      const c = clients.find((x) => x.client_id === r.client_id)
        || { client_id: r.client_id, company_name: r.company_name };
      setPreselect(c); setInvite(true);
    } else if (key === "delete" || key === "cancel") setConfirm(r);
  };

  const load = useCallback(async () => {
    try {
      const [r, b, o] = await Promise.all([
        axios.get(`${API}/register`), axios.get(`${API}/backlog`), axios.get(`${API}/overrides`),
      ]);
      setData(r.data); setBacklog(b.data); setOverrides(o.data);
    } catch (e) { toast.error("Could not load the Health Checks"); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menu]);
  useEffect(() => {
    axios.get(`${CS}/clients/register`).then((r) => {
      const rows = r.data.clients || [];
      setClients(rows);
      // Arriving from a client record's "Invite them" — open the preview for them.
      const wanted = new URLSearchParams(window.location.search).get("invite");
      if (wanted) {
        const ids = wanted.toUpperCase().split(",").map((s) => s.trim()).filter(Boolean);
        const picked = rows.filter((x) => ids.includes(x.client_id));
        if (picked.length) { setPreselect(picked.length === 1 ? picked[0] : picked); setInvite(true); }
      }
    }).catch(() => {});
  }, []);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    return band ? all.filter((r) => r.band === band) : all;
  }, [data, band]);

  const runMatches = useMemo(() => {
    const t = runQ.trim().toLowerCase();
    if (!t) return [];
    return clients.filter((c) => (c.company_name || "").toLowerCase().includes(t)
      || (c.client_id || "").toLowerCase().includes(t)).slice(0, 6);
  }, [runQ, clients]);

  const s = data?.summary || {};

  return (
    <div data-testid="health-checks-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 800, letterSpacing: "-0.018em", color: H.ink }}>
            Health Checks
          </h1>
          <div className="text-[13px] mt-1" style={{ color: H.ink2 }}>
            {s.submitted || 0} submitted · {s.outstanding || 0} outstanding · {s.open_pain_points || 0} pain
            points waiting on an answer
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setInvite(true)} data-testid="hc-invite-open"
            className="h-[34px] px-3.5 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase"
            style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.04em" }}>
            <Send className="w-3.5 h-3.5" /> Invite clients
          </button>
          <button onClick={() => setSettings(true)} data-testid="hc-settings-open"
            className="h-[34px] w-[34px] rounded-[7px] inline-flex items-center justify-center"
            style={{ background: "#fff", border: `1px solid ${H.control}`, color: H.ink2 }}>
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="rounded-[12px] overflow-hidden mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
        style={{ background: H.card, border: `1px solid ${H.border}` }} data-testid="hc-summary">
        <Num label="Submitted" value={s.submitted || 0} testid="hc-sum-submitted" />
        <Num label="Outstanding" value={s.outstanding || 0} testid="hc-sum-outstanding" />
        <Num label="Average score" value={s.average_score ?? "—"} testid="hc-sum-average" />
        <Num label="Green" value={s.green || 0} tone="#0B4F2E" testid="hc-sum-green" />
        <Num label="Amber" value={s.amber || 0} tone="#6B5600" testid="hc-sum-amber" />
        <Num label="Red" value={s.red || 0} tone="#8E1212" testid="hc-sum-red" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        {[["checks", "Checks"], ["backlog", `Pain points & feature asks${backlog?.overdue ? ` · ${backlog.overdue} over` : ""}`],
          ["overrides", `Overrides${overrides?.total ? ` · ${overrides.total}` : ""}`]].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`hc-view-${k}`}
            className="h-8 px-3 rounded-[7px] text-[12.5px] font-semibold"
            style={{ background: view === k ? H.ink : "#fff", color: view === k ? "#fff" : H.ink2,
              border: `1px solid ${view === k ? H.ink : H.control}` }}>
            {label}
          </button>
        ))}
        {view === "checks" && (
          <div className="flex items-center gap-1.5 ml-auto flex-wrap">
            {["Green", "Amber", "Red"].map((b) => (
              <button key={b} onClick={() => setBand(band === b ? null : b)} data-testid={`hc-filter-${b}`}
                className="h-8 px-2.5 rounded-[7px] text-[12px] font-bold uppercase"
                style={{ background: band === b ? H.ink : "#fff", color: band === b ? "#fff" : H.ink2,
                  border: `1px solid ${band === b ? H.ink : H.control}`, letterSpacing: "0.05em" }}>{b}</button>
            ))}
            <div className="relative">
              <input value={runQ} onChange={(e) => setRunQ(e.target.value)} data-testid="hc-run-search"
                placeholder="Run a check live…" className={`h-8 w-[190px] rounded-[7px] px-2.5 text-[12.5px] bg-white ${FOCUS}`}
                style={EDGE} />
              {runMatches.length > 0 && (
                <div className="absolute right-0 mt-1 w-[260px] rounded-[7px] overflow-hidden z-20"
                  style={{ background: "#fff", border: `1px solid ${H.border}` }}>
                  {runMatches.map((c) => (
                    <a key={c.client_id} href={`/health-check/${c.client_id}/staff`}
                      data-testid={`hc-run-${c.client_id}`}
                      className="block px-3 py-2 text-[13px] hover:bg-[#F6F4EF]" style={{ color: H.ink }}>
                      {c.company_name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {view === "checks" && (
        <div className="rounded-[12px] overflow-hidden" style={{ background: H.card, border: `1px solid ${H.border}` }}>
          <div className="overflow-x-auto">
            <div style={{ minWidth: 1000 }}>
              <div className="px-4 py-2.5 grid gap-3 text-[10.5px] font-bold uppercase tracking-[0.1em]"
                style={{ gridTemplateColumns: GRID, background: H.sunken, color: H.ink3, borderBottom: `1px solid ${H.divider}` }}>
                <div>Client</div><div>Invited</div><div>Tier</div><div>Submitted</div><div>Score</div>
                <div>Band</div><div>Flags</div><div>Next check</div>
              </div>
              {!data ? (
                <div className="px-4 py-6 text-[13px]" style={{ color: H.ink2 }}>Loading…</div>
              ) : rows.length === 0 ? (
                <div className="px-4 py-7" data-testid="hc-empty">
                  <div className="text-[14px] font-semibold" style={{ color: H.ink }}>No Health Checks yet.</div>
                  <div className="text-[13px] mt-1" style={{ color: H.ink2 }}>
                    Invite a client, or run one live from a client record.
                  </div>
                </div>
              ) : rows.map((r) => (
                <div key={r.client_id} className="px-4 py-3 grid gap-3 items-center relative cursor-pointer hover:bg-[#F9F7F2] transition-colors duration-150"
                  data-testid={`hc-row-${r.client_id}`} onClick={() => openRow(r)}
                  title={r.submission_id ? "Open the results" : "Fill it in on a call"}
                  style={{ gridTemplateColumns: GRID, borderBottom: `1px solid ${H.divider}` }}>
                  <div className="min-w-0">
                    <a href={`/clients/${r.client_id}`} onClick={(e) => e.stopPropagation()}
                      className="text-[13.5px] font-semibold block truncate"
                      style={{ color: H.ink }}>{r.company_name}</a>
                    <span className="mono text-[11px]" style={{ color: H.ink3 }}>{r.client_id}</span>
                  </div>
                  <div className="text-[12.5px]" style={{ color: H.ink2 }}>
                    {ddmon(r.invitation_date)}
                    {r.outstanding && (
                      <div className="text-[11px] font-bold uppercase" style={{ color: r.chased ? "#8E1212" : "#6B5600" }}>
                        {r.chased ? "Chased" : "Outstanding"}
                      </div>
                    )}
                  </div>
                  <div className="text-[12.5px] font-semibold" style={{ color: H.ink }}>{r.tier}</div>
                  <div className="text-[12.5px]" style={{ color: H.ink2 }}>{ddmon(r.submitted_at)}</div>
                  <div className="mono text-[13px] font-bold" style={{ color: H.ink }}>{r.score ?? "—"}</div>
                  <div>{r.band ? <BandChip band={r.band} testid={`hc-band-${r.client_id}`} /> : <span style={{ color: H.ink3 }}>—</span>}</div>
                  <div className="flex flex-wrap gap-1">
                    {(r.flags || []).map((f) => (
                      <span key={f} className="text-[10.5px] px-1.5 py-0.5 rounded-full"
                        style={{ background: H.sunken, border: `1px solid ${H.divider}`, color: H.ink2 }}>
                        {FLAG_LABELS[f] || f}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12.5px]" style={{ color: H.ink2 }}>{ddmon(r.next_check_date)}</span>
                    <button type="button" data-testid={`hc-dots-${r.client_id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const b = e.currentTarget.getBoundingClientRect();
                        setMenu(menu?.id === r.client_id ? null
                          : { id: r.client_id, top: b.top, bottom: b.bottom, right: b.right });
                      }}
                      className={`w-7 h-7 rounded-[7px] flex items-center justify-center ${FOCUS}`}
                      style={{ color: H.ink2, border: `1px solid ${menu?.id === r.client_id ? H.control : "transparent"}` }}>
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {view === "backlog" && <BacklogTable items={backlog?.items || []} />}

      {view === "overrides" && (
        <div className="rounded-[12px] overflow-hidden" style={{ background: H.card, border: `1px solid ${H.border}` }}
          data-testid="hc-overrides">
          {(overrides?.entries || []).length === 0 ? (
            <div className="px-4 py-6 text-[13px] font-semibold" style={{ color: H.ink }}>
              No staff overrides to review.
            </div>
          ) : overrides.entries.map((e, i) => (
            <div key={i} className="px-4 py-3" style={{ borderBottom: `1px solid ${H.divider}` }}>
              <div className="flex items-center gap-2 flex-wrap">
                <a href={`/clients/${e.client_id}`} className="text-[13.5px] font-semibold" style={{ color: H.ink }}>
                  {e.company_name}
                </a>
                <BandChip band={e.calculated_band} size={10.5} />
                <span style={{ color: H.ink3 }}>→</span>
                <BandChip band={e.final_band} size={10.5} />
                <span className="text-[12px]" style={{ color: H.ink3 }}>{e.by} · {ddmon(e.at)}</span>
              </div>
              <div className="text-[13px] mt-1" style={{ color: H.ink2 }}>{e.reason}</div>
            </div>
          ))}
        </div>
      )}

      {invite && <InviteDialog onClose={() => { setInvite(false); setPreselect(null); }}
        onSent={load} preselect={preselect} />}
      {settings && <SettingsDrawer onClose={() => setSettings(false)} />}
      {menu && rows.some((r) => r.client_id === menu.id) && (
        <RowMenu r={rows.find((r) => r.client_id === menu.id)} at={menu} onPick={rowAction} />
      )}
      {confirm && <ConfirmDelete row={confirm} busy={deleting}
        onCancel={() => setConfirm(null)} onConfirm={() => removeRow(confirm)} />}
    </div>
  );
}
